import type { IGameRecords } from '../interfaces/IGameRecords';
import type { IGame } from '../interfaces/IGame';
import type { IGameRecord } from '../interfaces/IGameRecord';
import { GameRecordBase, type GameRecordOptions } from './game-record-base';

export abstract class GameRecordsBase implements IGameRecords {
  AllRecord: IGameRecord;
  DivisionRecord: IGameRecord;
  ConferenceRecord: IGameRecord;
  HomeRecord: IGameRecord;
  AwayRecord: IGameRecord;
  WinningTeamsRecord: KnockoutComputed<IGameRecord>;
  LosingTeamsRecord: KnockoutComputed<IGameRecord>;
  Wins: KnockoutObservableArray<IGame>;
  Losses: KnockoutObservableArray<IGame>;
  Ties: KnockoutObservableArray<IGame>;
  teamName: string;
  fakeObs: KnockoutObservable<number | undefined>;
  subscribers: KnockoutSubscription[] = [];

  /** Override in subclasses to control GameRecord calculation behavior */
  protected get recordOptions(): Partial<GameRecordOptions> {
    return {};
  }

  constructor(
    Wins: KnockoutObservableArray<IGame>,
    Losses: KnockoutObservableArray<IGame>,
    Ties: KnockoutObservableArray<IGame>,
    teamName: string,
    fakeObs: KnockoutObservable<number | undefined>
  ) {
    this.fakeObs = fakeObs;
    this.teamName = teamName;
    this.Wins = Wins;
    this.Losses = Losses;
    this.Ties = Ties;

    this.AllRecord = new GameRecordBase(fakeObs, this.recordOptions);
    this.DivisionRecord = new GameRecordBase(fakeObs, this.recordOptions);
    this.ConferenceRecord = new GameRecordBase(fakeObs, this.recordOptions);
    this.HomeRecord = new GameRecordBase(fakeObs, this.recordOptions);
    this.AwayRecord = new GameRecordBase(fakeObs, this.recordOptions);

    this.WinningTeamsRecord = ko.computed(() => {
      const record = new GameRecordBase(fakeObs, this.recordOptions);
      ko.utils.arrayForEach(Wins(), (item: IGame) => {
        if (this.getOppPct(this.teamName, item) > 0.5) {
          record.Wins.increment();
        }
      });
      ko.utils.arrayForEach(Losses(), (item: IGame) => {
        if (this.getOppPct(this.teamName, item) > 0.5) {
          record.Losses.increment();
        }
      });
      ko.utils.arrayForEach(Ties(), (item: IGame) => {
        if (this.getOppPct(this.teamName, item) > 0.5) {
          record.Ties.increment();
        }
      });
      return record;
    });

    this.LosingTeamsRecord = ko.computed(() => {
      const record = new GameRecordBase(fakeObs, this.recordOptions);
      ko.utils.arrayForEach(Wins(), (item: IGame) => {
        if (this.getOppPct(this.teamName, item) <= 0.5) {
          record.Wins.increment();
        }
      });
      ko.utils.arrayForEach(Losses(), (item: IGame) => {
        if (this.getOppPct(this.teamName, item) <= 0.5) {
          record.Losses.increment();
        }
      });
      ko.utils.arrayForEach(Ties(), (item: IGame) => {
        if (this.getOppPct(this.teamName, item) <= 0.5) {
          record.Ties.increment();
        }
      });
      return record;
    });
  }

  initSubscribers(): void {
    const self = this;
    // game.home/away may be KO observable (function) or plain object depending on the data path
    const unwrap = (val: any) => typeof val === 'function' ? val() : val;

    self.subscribers.push(
      self.Wins.subscribe((arr) => {
        self.fakeObs();
        self.resetWins();
        self.AllRecord.Wins(arr.length);

        ko.utils.arrayForEach(arr, (item: IGame) => {
          const home = unwrap(item.home);
          const away = unwrap(item.away);
          if (!home || !away) return;
          if (home.Division === away.Division && home.Conference === away.Conference) {
            self.DivisionRecord.Wins.increment();
          }

          if (home.Conference === away.Conference) {
            self.ConferenceRecord.Wins.increment();
          }

          if (home.TeamName === self.teamName) {
            self.HomeRecord.Wins.increment();
          }

          if (away.TeamName === self.teamName) {
            self.AwayRecord.Wins.increment();
          }

          self.onWinAdded(item);
        });
      })
    );

    self.subscribers.push(
      self.Losses.subscribe((arr) => {
        self.fakeObs();
        self.resetLosses();
        self.AllRecord.Losses(arr.length);

        ko.utils.arrayForEach(arr, (item: IGame) => {
          const home = unwrap(item.home);
          const away = unwrap(item.away);
          if (!home || !away) return;
          if (home.Division === away.Division && home.Conference === away.Conference) {
            self.DivisionRecord.Losses.increment();
          }

          if (home.Conference === away.Conference) {
            self.ConferenceRecord.Losses.increment();
          }

          if (home.TeamName === self.teamName) {
            self.HomeRecord.Losses.increment();
          }

          if (away.TeamName === self.teamName) {
            self.AwayRecord.Losses.increment();
          }

          self.onLossAdded(item);
        });
      })
    );

    self.subscribers.push(
      self.Ties.subscribe((arr) => {
        self.fakeObs();
        self.resetTies();
        self.AllRecord.Ties(arr.length);

        ko.utils.arrayForEach(arr, (item: IGame) => {
          const home = unwrap(item.home);
          const away = unwrap(item.away);
          if (!home || !away) return;
          if (home.Division === away.Division && home.Conference === away.Conference) {
            self.DivisionRecord.Ties.increment();
          }

          if (home.Conference === away.Conference) {
            self.ConferenceRecord.Ties.increment();
          }

          if (home.TeamName === self.teamName) {
            self.HomeRecord.Ties.increment();
          }

          if (away.TeamName === self.teamName) {
            self.AwayRecord.Ties.increment();
          }

          self.onTieAdded(item);
        });
      })
    );

    self.Wins.valueHasMutated();
    self.Losses.valueHasMutated();
    self.Ties.valueHasMutated();
  }

  /** Hook for subclasses to handle extra logic on each win item (e.g. NHL OT/SO tracking) */
  protected onWinAdded(_item: IGame): void { /* base: no-op */ }
  /** Hook for subclasses to handle extra logic on each loss item */
  protected onLossAdded(_item: IGame): void { /* base: no-op */ }
  /** Hook for subclasses to handle extra logic on each tie/OT item */
  protected onTieAdded(_item: IGame): void { /* base: no-op */ }

  valueHasMutated(): void {
    this.Wins.valueHasMutated();
    this.Losses.valueHasMutated();
    this.Ties.valueHasMutated();
  }

  getOppPct(teamName: string, game: IGame): number {
    // game.home may be an observable (function) or a plain object depending on the code path
    const home = typeof game.home === 'function' ? game.home() : game.home;
    const away = typeof game.away === 'function' ? game.away() : game.away;
    if (!home || !away) return 0;
    if (home.TeamName === teamName) {
      return typeof away.Pct === 'function' ? away.Pct() : 0;
    } else {
      return typeof home.Pct === 'function' ? home.Pct() : 0;
    }
  }

  resetWins(): void {
    this.AllRecord.resetWins();
    this.DivisionRecord.resetWins();
    this.ConferenceRecord.resetWins();
    this.HomeRecord.resetWins();
    this.AwayRecord.resetWins();
  }

  resetLosses(): void {
    this.AllRecord.resetLosses();
    this.DivisionRecord.resetLosses();
    this.ConferenceRecord.resetLosses();
    this.HomeRecord.resetLosses();
    this.AwayRecord.resetLosses();
  }

  resetTies(): void {
    this.AllRecord.resetTies();
    this.DivisionRecord.resetTies();
    this.ConferenceRecord.resetTies();
    this.HomeRecord.resetTies();
    this.AwayRecord.resetTies();
  }
}
